"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";
import { cn } from "@/lib/utils";
import { type AsChildProps, resolveRenderProp } from "./render-prop";

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root {...props} />;
}

function PopoverTrigger({
  asChild = false,
  children,
  nativeButton,
  render,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger> & AsChildProps) {
  const renderProps = resolveRenderProp({
    asChild,
    children,
    nativeButton,
    render,
  });

  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      nativeButton={renderProps.nativeButton}
      render={renderProps.render}
      {...props}
    >
      {renderProps.children}
    </PopoverPrimitive.Trigger>
  );
}

function PopoverAnchor({
  asChild = false,
  children,
  className,
  render,
  ...props
}: useRender.ComponentProps<"div"> & AsChildProps) {
  const renderProps = resolveRenderProp({ asChild, children, render });

  return useRender({
    defaultTagName: "div",
    render: renderProps.render,
    props: mergeProps<"div">(
      {
        className,
        "data-slot": "popover-anchor",
      } as React.ComponentProps<"div">,
      { ...props, children: renderProps.children },
    ),
  });
}

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof PopoverPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset"
  > & {
    onCloseAutoFocus?: (event: Event) => void;
    onOpenAutoFocus?: (event: Event) => void;
    /**
     * Classes for the Positioner, which is what carries the stacking layer.
     * `className` lands on the popup *inside* an `isolation: isolate` wrapper,
     * so a `z-*` there can never lift the popover above a sibling overlay —
     * use this to override `--z-popup` for one call site.
     */
    positionerClassName?: string;
  };

function PopoverContent({
  align,
  alignOffset,
  className,
  positionerClassName,
  side,
  sideOffset = 4,
  ...props
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        // Portaled into <body>, which an open vaul Drawer sets to
        // `pointer-events: none`; without this every tap on an option falls
        // through to whatever sits behind the popup.
        className={cn(
          "pointer-events-auto isolate z-(--z-popup)",
          positionerClassName,
        )}
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          className={cn(
            "w-72 origin-[var(--transform-origin)] rounded-2xl border border-black/15 bg-black/80 p-4 text-text-primary opacity-100 shadow-[0_15px_30px_rgba(0,0,0,0.5),inset_0_1px_1.5px_rgba(255,255,255,0.15)] backdrop-blur-xl transition-[opacity,transform] duration-200 outline-none data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 dark:border-white/10 dark:bg-black/90",
            className,
          )}
          data-slot="popover-content"
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
